import React, {useState, useEffect, useRef} from "react";
import {useAuth} from '../context/AuthContext';
import {useNavigate} from 'react-router-dom';
import {db} from '../firebase';
import {collection, query,where,orderBy,onSnapshot,doc,setDoc,onSnapshot as docOnSnapshot} from 'firebase/firestore';
import ReactMarkdown from 'react-markdown';
import './DnDChat.css';

const StatInput = ({ label, name, type = 'text', value, onChange, className = '' }) => (
    <div className={`stat-block ${className}`}>
        <input
            id={name}
            name={name}
            type={type}
            value={value}
            onChange={onChange}
            className="stat-input-value"
            placeholder=""
        />
        <label htmlFor={name} className="stat-label">{label}</label>
    </div>
);

export function ChatBot() {
    const {currentUser, logout} = useAuth();
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [prompt, setPrompt] = useState([]);
    const [loading, setLoading] = useState(false);
    const [diceResult, setDiceResult] = useState(null);
    const [activeTab, setActiveTab] = useState('journal')
    const [journalText, setJournalText] = useState("");

    const [characterStats, setCharacterStats] = useState({
        name: "",
        class: "",
        subclass: "",
        level: 1,
        hpCurrent: 10,
        hpMax: 10,
        ac: 10,
        str: 10,
        dex: 10,
        con: 10,
        int: 10,
        wis: 10,
        cha: 10,
    });

    const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
    const tavernRef = useRef(null);
    const townRef = useRef(null);
    const forestRef = useRef(null);
    const dungeonRef = useRef(null);
    const peacefulRef = useRef(null);
    const tenseRef = useRef(null);
    const combatRef = useRef(null);
    const bossRef = useRef(null);

    const audioThemes = [
        { name: 'Tavern', ref: tavernRef, src: '/audio/tavern.mp3' },
        { name: 'Town', ref: townRef, src: '/audio/town.mp3' },
        { name: 'Nature', ref: forestRef, src: '/audio/nature.mp3' },
        { name: 'Dungeon', ref: dungeonRef, src: '/audio/dungeon.mp3' },
        { name: 'Peaceful', ref: peacefulRef, src: '/audio/peaceful.mp3' },
        { name: 'Suspenseful', ref: tenseRef, src: '/audio/suspenseful.mp3' },
        { name: 'Combat', ref: combatRef, src: '/audio/combat.mp3' },
        { name: 'Boss Battle', ref: bossRef, src: '/audio/boss.mp3' },
    ];

    // Enables autoscrolling
    const messagesEndRef = useRef(null);

    // Handles changing user's stats
    const handleStatChange = (e) => {
        const { name, value, type } = e.target;
        const val = type === 'number' ? (parseInt(value, 10) || 0) : value;
        setCharacterStats(prevStats => ({
            ...prevStats,
            [name]: val
        }));
    };

    // Effect for chat messages
    useEffect (() => {
        if (!currentUser) return;
        const q = query(collection(db, "chats"),
            where("userId","==", currentUser.uid),
            orderBy("timestamp","asc")
        );

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const newMessages = [];
            querySnapshot.forEach((doc) => {
                newMessages.push(doc.data());
            });
            setMessages(newMessages);
        });
        return () => unsubscribe();
    }, [currentUser]);

    // Effect for loading journal
    useEffect(() => {
        if (!currentUser) return;
        const journalDocRef = doc(db, 'users', currentUser.uid, 'journal', 'notes');
        const unsubscribe = docOnSnapshot(journalDocRef, (doc) => {
            if (doc.exists()) {
                setJournalText(doc.data().content || "");
            } else {
                setJournalText("");
            }
        });
        return () => unsubscribe();
    }, [currentUser]);

    // Effect for saving journal
    useEffect(() => {
        if (!currentUser) return;
        const saveTimer = setTimeout(() => {
            const journalDocRef = doc(db, 'users', currentUser.uid, 'journal', 'notes');
            const dataToSave = {
                content: journalText,
                userId: currentUser.uid 
            };
            setDoc(journalDocRef, dataToSave, {merge: true})
                .catch(err => console.error("Error saving journal:", err));
        }, 1500);
        return () => clearTimeout(saveTimer);
    }, [journalText, currentUser]);

    // Effect for chat scrolling
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({behavior:"smooth"});
    }, [messages]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!prompt.trim()) return;
        setLoading(true);
        const currentPrompt = prompt;
        setPrompt('');
        
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch('https://chatbot-p15m.onrender.com/api/chat', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({prompt: currentPrompt})
            });
            if (!response.ok) {
                throw new Error('Failed to get response from server.');
            }
        } catch (error) {
            console.error("Error sending message:", error);
        }
        setLoading(false);
    };

    const rollDice = (sides) => {
        const result = Math.floor(Math.random() * sides) + 1;
        setDiceResult({roll: result, sides: sides});
    }

    // Handles making a new chat
    const handleNewChat = async () => {
        if (!window.confirm("Are you certain you wish to start a new quest? Your current journey will be lost to time...")) {
            return;
        }
        try {
            setLoading(true);
            const token = await currentUser.getIdToken();
            const response = await fetch('https://chatbot-p15m.onrender.com/api/chat/new', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) {
                throw new Error('Failed to clear chat history.');
            }
        } catch (error) {
            console.error("Failed to start new chat:", error);
        }
        setLoading(false);
    };

    // Handles logging out of your account
    const handleLogout = async () => {
        try {
            await logout();
            navigate('/');
        } catch (error) {
            console.error("failed to log out", error);
        }
    }

    // Plays selected audio
    const playAudio = (audioRef) => {
        // Stops audio
        if (currentlyPlaying && currentlyPlaying !== audioRef.current) {
            currentlyPlaying.pause();
            currentlyPlaying.currentTime = 0;
        }
        // Pauses audio
        if (audioRef.current === currentlyPlaying) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setCurrentlyPlaying(null);
        // Plays on loop
        } else {
            audioRef.current.loop = true;
            audioRef.current.volume = 0.4;
            audioRef.current.play();
            setCurrentlyPlaying(audioRef.current);
        }
    };

    const stopAudio = () => {
        if (currentlyPlaying) {
            currentlyPlaying.pause();
            currentlyPlaying.currentTime = 0;
            setCurrentlyPlaying(null);
        }
    };

    return (
    <div className="main">
        <header className="header">
            <h1 className="header-title">
                AI Dungeon Master
            </h1>
            <button id='logout' onClick = {handleLogout}>Logout</button>
        </header>
        <div className='main-content'>
            <div className="chat-column">
                <div className="sticky-chat-area">
                    <div className="features">
                        <button id='new-chat' onClick = {handleNewChat} disabled={loading}>Start a New Quest</button>
                    </div>
                    <div className="scroll-container">
                        <div className="chat-window">
                            {messages.length === 0 && !loading ? (
                                <div className="suggestions">
                                    <h3 className="suggestions-title">Need help, Adventurer?</h3>
                                    <p>Try asking your DM:</p>
                                    <ul className="suggestion-list">
                                        <li>"Can you help me build a character?"</li>
                                        <li>"Set the scene for a campaign."</li>
                                        <li>"What are the rules for using cover?"</li>
                                    </ul>
                                </div>
                            ) : (
                                messages.map((msg,index) => (
                                <div key={index} className={`message ${msg.role}`}>
                                    <strong>{msg.role === 'user' ? 'You' : 'DM'}:</strong>
                                    <ReactMarkdown>{msg.text}</ReactMarkdown>
                                </div>
                                ))
                            )}
                            {loading && <p className="loading">The DM is thinking...</p>}
                            <div ref={messagesEndRef}></div>
                        </div>
                    </div>
                    <form onSubmit={handleSubmit} className='form'>
                        <input
                            type="text"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="State your intentions here"
                            disabled={loading}
                        />
                        <button type="submit" disabled={loading}>Send</button>
                    </form>
                </div>
            </div>
            <div className="dice-column">
                <h2 className="dice-title">Dice Roller</h2>
                {diceResult && (
                    <div className="dice-result-container">
                        <span className="dice-result-label">You rolled a d{diceResult.sides}:</span>
                        <span className="dice-result-value">{diceResult.roll}</span>
                    </div>
                )}

                <div className="dice-grid">
                    <button className="dice-button" onClick={() => rollDice(20)}>d20</button>
                    <button className="dice-button" onClick={() => rollDice(12)}>d12</button>
                    <button className="dice-button" onClick={() => rollDice(10)}>d10</button>
                    <button className="dice-button" onClick={() => rollDice(8)}>d8</button>
                    <button className="dice-button" onClick={() => rollDice(6)}>d6</button>
                    <button className="dice-button" onClick={() => rollDice(4)}>d4</button>
                </div>
                <div className="tab-navigation">
                    <button onClick={() => setActiveTab('journal')}>Journal</button>
                    <button onClick={() => setActiveTab('stats')}>Stats</button>
                    <button onClick={() => setActiveTab('audio')}>Audio</button>
                </div>
                <div className="tab-content">
                    {activeTab === 'journal' && (
                        <div className="journal-content">
                            <h3>Your Journal</h3>
                            <textarea className="text-area"
                                placeholder="Your notes... (autosaves)"
                                value = {journalText}
                                onChange={(e) => setJournalText(e.target.value)}>
                                </textarea>
                        </div>
                    )}
                    {activeTab === 'stats' && (
                    <div className="stats-content">
                        <h3>Character Sheet</h3>
                        <div className="character-info-grid">
                            <div className="stat-block-long">
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    value={characterStats.name}
                                    onChange={handleStatChange}
                                    className="stat-input-value"
                                    placeholder="Character Name"
                                />
                                <label htmlFor="name" className="stat-label">Name</label>
                            </div>
                            <StatInput label="Class" name="characterClass" value={characterStats.class} onChange={handleStatChange} />
                            <StatInput label="Level" name="level" type="number" value={characterStats.level} onChange={handleStatChange} />
                        </div>
                        <div className="core-stats-grid">
                            <StatInput label="Armor Class" name="armorClass" type="number" value={characterStats.ac} onChange={handleStatChange} />
                            <StatInput label="HP (Current)" name="hpCurrent" type="number" value={characterStats.hpCurrent} onChange={handleStatChange} />
                            <StatInput label="HP (Max)" name="hpMax" type="number" value={characterStats.hpMax} onChange={handleStatChange} />
                        </div>
                        <h3 className="stats-header">Ability Scores</h3>
                        <div className="ability-scores-grid">
                            <StatInput label="Strength" name="strength" type="number" value={characterStats.str} onChange={handleStatChange} />
                            <StatInput label="Dexterity" name="dexterity" type="number" value={characterStats.dex} onChange={handleStatChange} />
                            <StatInput label="Constitution" name="constitution" type="number" value={characterStats.con} onChange={handleStatChange} />
                            <StatInput label="Intelligence" name="intelligence" type="number" value={characterStats.int} onChange={handleStatChange} />
                            <StatInput label="Wisdom" name="wisdom" type="number" value={characterStats.wis} onChange={handleStatChange} />
                            <StatInput label="Charisma" name="charisma" type="number" value={characterStats.cha} onChange={handleStatChange} />
                        </div>
                    </div>
                    )} 
                    {activeTab === 'audio' && (
                    <div className="audio-content">
                        {/* --- Hidden Audio Elements ---
                            These are the actual audio players. The 'src' paths
                            come from the 'audioThemes' array you defined earlier.
                        */}
                        {audioThemes.map(theme => (
                            <audio key={theme.name} ref={theme.ref} src={theme.src} preload="auto" />
                        ))}
                        
                        {/* --- Audio Control Buttons --- */}
                        <h3 className="stats-header">Ambient Themes</h3>
                        <div className="audio-grid">
                            {audioThemes.map(theme => (
                                <button
                                    key={theme.name}
                                    className={`audio-button ${currentlyPlaying === theme.ref.current ? 'playing' : ''}`}
                                    onClick={() => playAudio(theme.ref)}
                                >
                                    {theme.name}
                                </button>
                            ))}
                        </div>
                        <button
                            className="audio-button stop-button"
                            onClick={stopAudio}
                        >
                            Stop Music
                        </button>
                    </div>
                    )}
                </div>
            </div>
        </div>
    </div>);
}

export default ChatBot;